import React from "react";
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import './PageLayout.css';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import globals from '../Globals'
import TitlesPage from "../Pages/TitlesPage";
import AdminsPage from "../Pages/AdminsPage";
import DatabasePage from "../Pages/DatabasePage";
import AssetsPage from "../Pages/AssetsPage";
import TestPage from "../Pages/TestPage";
import EventBus from "../EventBus";
import ConfirmLogoutDialog from './ConfirmLogoutDialog';
import AssetInspector from "../Pages/AssetInspector";
import MachineSpecs from "../Pages/MachineSpecs";
import DeployPage from "../Pages/DeployPage";

export const Page = {
    // Global pages
    Dropdown: { id: 0, title: "Dropdown", subs: {
        Admins: { id: 0, title: "Admins" },
        Logout: { id: 1, title: "Logout" },
    }},
    Titles: { id: 1, title: "Game Titles", subs: {} },

    // Title-scoped pages
    Home: { id: 2, title: "Home", subs: {} },
    Players: { id: 3, title: "Players", subs: {} },
    Servers: { id: 4, title: "Game Servers", subs: {
        Overview: { id: 0, title: "Overview" },
        Machines: { id: 1, title: "Machine Specs" },
        Scaling: { id: 2, title: "Scaling Rules" },
        Deploy: { id: 3, title: "Manual Deploy" },
        Logs: { id: 4, title: "Logs" },
    }, required: { permissions: ["servers"], mode: "any"}},
    Database: { id: 5, title: "Database", subs: {
        Versioning: { id: 0, title: "Versioning",
            required: { permissions: ["database", "servers"], mode: "any"} },
        Assets: { id: 1, title: "Asset Manager",
            required: { permissions: ["database"], mode: "any"} },
        AssetInspector: { id: 2, title: "Asset Inspector",
            required: { permissions: ["database"], mode: "any"}}
    }},
    Test: { id: 6, title: "Test", subs: {} }
}

export default class PageLayout extends React.Component {
    state = {
        currentPage: Page.Home.id,
        currentSub: 0,
        menuOpen: true,
        confirmLogoutOpen: false,
        selectedTitleVersion: 0,
        dropdownAnchorEl: null,
    }

    closeConfirmDialog = () => {
        this.setState({ confirmLogoutOpen: false });
    }

    handleLogout = () => {
        this.setState({ confirmLogoutOpen: false }, () => {
            EventBus.emit("logout", false);
        });
    }

    handleForgetAndLogout = () => {
        this.setState({ confirmLogoutOpen: false }, () => {
            EventBus.emit("logout", true);
        });
    }

    toggleMenu = () => {
        this.setState({ menuOpen: !this.state.menuOpen });
    }

    openDropdown = (e) => {
        this.setState({ dropdownAnchorEl: e.currentTarget });
    }

    closeDropdown = () => {
        this.setState({ dropdownAnchorEl: null });
    }

    dropdownItemClicked = (subId) => {
        this.closeDropdown();

        switch (subId) {
            case Page.Dropdown.subs.Admins.id:
                this.buttonClicked(Page.Dropdown.id, Page.Dropdown.subs.Admins.id);
                return;
            case Page.Dropdown.subs.Logout.id:
                this.setState({ confirmLogoutOpen: true });
                return;
            default:
                return;
        }
    }

    buttonClicked = (page, sub) => {
        // Never allow opening a title-scoped page without a selected title
        if (this.isTitleScopedPage(page)) {
            const ok = this.ensureSelectedTitle();
            if (!ok) {
                this.redirectToTitlesPage();
                return;
            }
        }

        if (!this.ensurePagePermission(page, sub)) {
            return;
        }

        this.setState({ currentPage: page, currentSub: sub });

        const newHash = `${page}-${sub}`;
        window.location.hash = newHash;
    }

    isTitleScopedPage = (pageId) => {
        return pageId >= Page.Home.id;
    }

    getSelectedTitleRoles = () => {
        const titleId = globals.selectedTitle;
        const rolesByTitle = globals.loginData?.titles?.roles;
        if (!titleId || !rolesByTitle) return new Set();

        const raw = rolesByTitle[titleId];

        // Backend may serialize HashSet as array, or as object-map. Support both.
        if (Array.isArray(raw)) return new Set(raw);
        if (raw && typeof raw === "object") return new Set(Object.keys(raw));

        return new Set();
    }

    hasRequiredPermissions = (required) => {
        // No requirements => always allowed
        if (!required || !required.permissions || required.permissions.length === 0) return true;

        const roles = this.getSelectedTitleRoles();
        const mode = required.mode ?? "any"; // "any" or "all"

        const hasPerm = (perm) => roles.has(perm) || roles.has(`#${perm}`);

        if (mode === "all") {
            return required.permissions.every(hasPerm);
        }

        // default any
        return required.permissions.some(hasPerm);
    }

    isSubVisible = (page, sub) => {
        // If sub has required, enforce it; if not set, always visible
        if (!sub || !sub.required) return true;
        return this.hasRequiredPermissions(sub.required);
    }

    isPageVisible = (page) => {
        // If page has explicit required, enforce it
        if (page.required) {
            return this.hasRequiredPermissions(page.required);
        }

        // If page has no required and has no subs => always visible
        const subs = page.subs ? Object.values(page.subs) : [];
        if (subs.length === 0) return true;

        // Root pages without required: show only if any child sub is visible
        return subs.some(sub => this.isSubVisible(page, sub));
    }

    ensurePagePermission = (pageId, subId) => {
        const page = Object.values(Page).find(p => p.id === pageId);
        if (!page) return true;

        // Global pages: if required is set, check it; otherwise allow
        if (!this.isTitleScopedPage(pageId)) {
            if (page.required && !this.hasRequiredPermissions(page.required)) {
                return false;
            }
            return true;
        }

        // Title-scoped pages require selectedTitle and permission visibility
        if (!globals.selectedTitle) return false;

        if (!this.isPageVisible(page)) return false;

        // If navigating to a sub, ensure the sub is visible
        const subs = page.subs ? Object.values(page.subs) : [];
        const sub = subs.find(s => s.id === subId);
        if (sub && !this.isSubVisible(page, sub)) return false;

        return true;
    }

    ensureSelectedTitle = () => {
        const titlesDict = globals.loginData?.titles?.titles ?? {};
        const titleIds = Object.keys(titlesDict);

        // If already selected, ensure it is still valid
        if (globals.selectedTitle && titleIds.includes(globals.selectedTitle)) {
            return true;
        }

        // Try restore from persistent storage
        const stored = localStorage.getItem("selectedTitle");
        if (stored && titleIds.includes(stored)) {
            globals.selectedTitle = stored;
            this.setState({ selectedTitleVersion: this.state.selectedTitleVersion + 1 });
            return true;
        }

        // If there is exactly one possible title, auto-select it
        if (titleIds.length === 1) {
            globals.selectedTitle = titleIds[0];
            localStorage.setItem("selectedTitle", globals.selectedTitle);
            this.setState({ selectedTitleVersion: this.state.selectedTitleVersion + 1 });
            return true;
        }

        // No valid selection
        globals.selectedTitle = null;
        localStorage.removeItem("selectedTitle");
        this.setState({ selectedTitleVersion: this.state.selectedTitleVersion + 1 });
        return false;
    }

    redirectToTitlesPage = () => {
        // Titles page is global
        const newHash = `${Page.Titles.id}-0`;
        if (window.location.hash.replace("#", "") !== newHash) {
            window.location.hash = newHash;
        }
        this.setState({ currentPage: Page.Titles.id, currentSub: 0 });
    }

    generateButtons(){
        let buttons = [];

        for (const page of Object.values(Page)) {
            // Dropdown is shown in the top bar, not in the side menu
            if (page.id === Page.Dropdown.id) continue;
            // Hide title-scoped pages until a title is selected
            if (this.isTitleScopedPage(page.id) && !globals.selectedTitle) {
                continue;
            }

            // Hide pages the user cannot access
            if (!this.isPageVisible(page)) {
                continue;
            }

            let active = this.state.currentPage === page.id;

            let classes = "side-menu-button";
            if (active) classes += " side-menu-button-current";

            buttons.push(
                <div key={page.id} className={classes} onClick={() => this.buttonClicked(page.id, 0)}>
                    <div><span>{page.title}</span></div>
                </div>
            );

            // Separator: global pages (Game Titles) vs title-scoped pages (Home and below)
            if (page.id === Page.Titles.id) {
                buttons.push(<div key="menu-separator-global" className="side-menu-separator" />);
            }

            if (active) {
                for (const sub of Object.values(page.subs)) {
                    if (!this.isSubVisible(page, sub)) continue;

                    let key = page.id + "-" + sub.id;

                    let subClasses = "side-menu-button side-submenu-button";
                    if (this.state.currentSub === sub.id) subClasses += " side-menu-button-current";

                    buttons.push(
                        <div key={key} className={subClasses} onClick={() => this.buttonClicked(page.id, sub.id)}>
                            <div><span>{sub.title}</span></div>
                        </div>
                    );
                }
            }
        }

        return buttons;
    }

    generateMenu = () => {
        if (this.state.menuOpen) {
            return (
                <div id="side-menu">
                    {this.generateButtons()}
                </div>
            );
        } else {
            return (<></>);
        }
    }

    routePage() {
        // Defensive: never render scoped pages without a selected title
        if (this.isTitleScopedPage(this.state.currentPage)) {
            const ok = this.ensureSelectedTitle();
            if (!ok) {
                return <TitlesPage key="titlesPage" />;
            }
        }

        switch (this.state.currentPage) {
            case Page.Titles.id:
                return <TitlesPage key="titlesPage" />;
            case Page.Dropdown.id:
                switch (this.state.currentSub) {
                    case Page.Dropdown.subs.Admins.id:
                        return <AdminsPage key="adminsPage" />;
                    default:
                        return <h2>Dropdown: Empty Page</h2>;
                }
            case Page.Home.id:
                return <h2>Overview</h2>;
            case Page.Servers.id:
                switch (this.state.currentSub) {
                    case Page.Servers.subs.Overview.id:
                        return <h2>Servers Overview</h2>;
                    case Page.Servers.subs.Machines.id:
                        return <MachineSpecs key="MachineSpecs" />;
                    case Page.Servers.subs.Scaling.id:
                        return <h2>Scaling</h2>;
                    case Page.Servers.subs.Deploy.id:
                        return <DeployPage key="DeployPage" />;
                    case Page.Servers.subs.Logs.id:
                        return <h2>Logs</h2>;
                    default:
                        return <h2>Servers: Empty Page</h2>;
                }
            case Page.Database.id:
                switch (this.state.currentSub) {
                    case Page.Database.subs.Versioning.id:
                        return <DatabasePage key="databasePage" />;
                    case Page.Database.subs.Assets.id:
                        return <AssetsPage key="assetsPage" />;
                    case Page.Database.subs.AssetInspector.id:
                        return <AssetInspector key="assetInspector" />;
                    default:
                        return <h2>Database: Empty Page</h2>;
                }
            case Page.Test.id:
                return <TestPage key="testPage" />;
            default:
                return <h2>Empty Page</h2>;
        }
    }

    render() {
        return (
            <>
                <div id="top-menu">
                    <IconButton
                        id="top-menu-button"
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        style={{ marginRight: '12px' }}
                        onClick={this.toggleMenu}
                    >
                        <MenuIcon />
                    </IconButton>
                    <span id="top-menu-title">
                        {globals.selectedTitle
                            ? (globals.loginData?.titles?.titles?.[globals.selectedTitle]?.title
                                ?? globals.loginData?.titles?.titles?.[globals.selectedTitle]?.name
                                ?? globals.selectedTitle)
                            : "Huge Loot"}
                    </span>
                    <span id="top-menu-logout" onClick={this.openDropdown}>
                        Menu
                    </span>
                    <Menu
                        anchorEl={this.state.dropdownAnchorEl}
                        open={Boolean(this.state.dropdownAnchorEl)}
                        onClose={this.closeDropdown}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {Object.values(Page.Dropdown.subs).map((sub) => (
                            <MenuItem key={sub.id} onClick={() => this.dropdownItemClicked(sub.id)}>
                                {sub.title}
                            </MenuItem>
                        ))}
                    </Menu>
                    <span id="top-menu-email">{globals.loginData.name}</span>
                </div>
                {this.generateMenu()}
                <div
                    id="page-content"
                    align="center"
                    className={this.state.menuOpen ? "page-content-margin" : ""}
                >
                    {this.routePage()}
                </div>
                <ConfirmLogoutDialog
                    open={this.state.confirmLogoutOpen}
                    onClose={this.closeConfirmDialog}
                    onLogout={this.handleLogout}
                    onForgetAndLogout={this.handleForgetAndLogout}
                />
            </>
        );
    }

    componentDidMount() {
        window.addEventListener("hashchange", this.handleHashChange);

        // If title is not set: try restore, otherwise auto-select if only one title.
        // If still not selected and user is heading to a title-scoped page, force Titles page.
        if (!globals.selectedTitle) {
            this.ensureSelectedTitle();
        }

        this.handleHashChange();
    }

    componentWillUnmount() {
        window.removeEventListener("hashchange", this.handleHashChange);
    }

    handleHashChange = () => {
        const hash = window.location.hash.replace("#", "");
        const [pageStr, subStr] = hash.split("-");

        const parsedPage = parseInt(pageStr, 10);
        const parsedSub = parseInt(subStr, 10);

        const page = Number.isNaN(parsedPage) ? Page.Home.id : parsedPage;
        const sub = Number.isNaN(parsedSub) ? 0 : parsedSub;

        // Never allow opening a title-scoped page without a selected title
        if (this.isTitleScopedPage(page)) {
            const ok = this.ensureSelectedTitle();
            if (!ok) {
                // If more than one title exists (or none), force Titles page
                this.redirectToTitlesPage();
                return;
            }
        }

        // Permission enforcement: do not allow navigating to pages/subpages without roles
        if (!this.ensurePagePermission(page, sub)) {
            // Prefer Home for scoped pages, otherwise Titles
            if (this.isTitleScopedPage(page)) {
                window.location.hash = `${Page.Home.id}-0`;
                this.setState({ currentPage: Page.Home.id, currentSub: 0 });
            } else {
                this.redirectToTitlesPage();
            }
            return;
        }

        this.setState({ currentPage: page, currentSub: sub });
    }
}